import { Checkbox, Tag, Tooltip } from 'antd'
import { Check, LockKeyhole } from 'lucide-react'
import {
  permissionByCode,
  type PermissionCode,
  type PermissionDefinition
} from '@super-mega/server/contracts/authorization'

type Props = {
  module: string
  entries: PermissionDefinition[]
  selected: Set<PermissionCode>
  disabled?: boolean
  protectedRole?: boolean
  searching: boolean
  onToggle: (codes: PermissionCode[], checked: boolean) => void
}

export function PermissionModule({ module, entries, selected, disabled, protectedRole, searching, onToggle }: Props) {
  const checklist = (items: PermissionDefinition[]) => {
    const editable = items.filter((entry) => !entry.superAdminOnly).map((entry) => entry.code as PermissionCode)
    const count = editable.filter((code) => selected.has(code)).length
    return { editable, count, all: editable.length > 0 && count === editable.length }
  }
  const state = checklist(entries)
  return (
    <section aria-label={`${module}权限`} className="min-w-0 p-4 sm:p-5">
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b pb-4">
        <div className="flex items-baseline gap-3">
          <h3 className="text-content m-0 text-base font-semibold">{module}</h3>
          <span className="text-content-soft text-xs">
            {entries.length} 项{searching ? '匹配权限' : '权限'}
          </span>
        </div>
        {protectedRole ? (
          <span className="text-content-muted flex items-center gap-1.5 text-xs">
            <LockKeyhole className="size-3.5" />
            系统授权
          </span>
        ) : (
          <Checkbox
            disabled={disabled || !state.editable.length}
            checked={state.all}
            indeterminate={state.count > 0 && !state.all}
            onChange={(event) => onToggle(state.editable, event.target.checked)}
          >
            {searching ? '选择本模块搜索结果' : '模块全选'}
          </Checkbox>
        )}
      </div>
      {(['route', 'read', 'write'] as const).map((category) => {
        const items = entries.filter((entry) => {
          if (category === 'route') return entry.kind === 'route'
          return entry.kind === 'operation' && entry.effect === category
        })
        if (!items.length) return null
        const categoryState = checklist(items)
        const label = { route: '页面入口', read: '读取操作', write: '编辑操作' }[category]
        return (
          <div key={category} className="border-border border-b py-4 last:border-b-0 last:pb-0">
            <div className="mb-3 flex items-center gap-2">
              {protectedRole ? (
                <h4 className="text-content-muted m-0 text-xs font-medium">{label}</h4>
              ) : (
                <Checkbox
                  disabled={disabled || !categoryState.editable.length}
                  checked={categoryState.all}
                  indeterminate={categoryState.count > 0 && !categoryState.all}
                  onChange={(event) => onToggle(categoryState.editable, event.target.checked)}
                >
                  {label}
                </Checkbox>
              )}
              <span className="text-content-soft text-xs tabular-nums">{items.length}</span>
            </div>
            <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((entry) => (
                <Tooltip
                  key={entry.code}
                  title={
                    <div>
                      <div>{entry.code}</div>
                      {entry.requires.length > 0 && (
                        <div>依赖：{entry.requires.map((code) => permissionByCode.get(code)?.name).join('、')}</div>
                      )}
                    </div>
                  }
                >
                  <div className="text-content flex min-w-0 items-start gap-2 py-1.5 text-sm">
                    {protectedRole ? (
                      <span className="flex items-start gap-2">
                        <Check aria-label="已授权" className="text-accent mt-0.5 size-4 shrink-0" />
                        {entry.name}
                      </span>
                    ) : (
                      <Checkbox
                        checked={selected.has(entry.code as PermissionCode)}
                        disabled={disabled || entry.superAdminOnly}
                        onChange={(event) => onToggle([entry.code as PermissionCode], event.target.checked)}
                      >
                        {entry.name}
                      </Checkbox>
                    )}
                    {entry.superAdminOnly && (
                      <Tag className="m-0! shrink-0" variant="filled">
                        仅超管
                      </Tag>
                    )}
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        )
      })}
    </section>
  )
}
